import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import { toast } from '@app/components/ui/Toast/toast'
import { userAtom } from '@app/hooks/useUserProfile'
import { UserRole } from '@app/types'
import { getBaseLayerId } from '@domains/utils/layers.utils'
import CreateIcon from '@mui/icons-material/Create'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDeleteLayerSuitability } from '../hooks/useDeleteLayerSuitability'
import { useFetchLayerSuitability } from '../hooks/useFetchLayerSuitability'
import { SuitabilityBadge } from './SuitabilityBadge'

export const SuitabilityReadMode = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { layerId } = useParams<{ layerId: string }>()
  const normalizedId = layerId ? getBaseLayerId(layerId) : undefined
  const { data: suitability = [], isLoading, isError } = useFetchLayerSuitability(normalizedId)
  const { mutate: deleteSuitability, isPending } = useDeleteLayerSuitability(normalizedId ?? '')
  const { data: user } = useAtomValue(userAtom)
  const isAdmin = user?.user_roles.includes(UserRole.ADMIN)

  const currentUseCase = searchParams.get('current')
  const [deleteUseCaseSlug, setDeleteUseCaseSlug] = useState<string | null>(null)

  if (isLoading) return <Typography color="text.secondary">Loading suitability...</Typography>
  if (isError) return <Typography color="error">Failed to load suitability.</Typography>
  if (!suitability.length)
    return (
      <EmptyData
        title="No suitability information available for this layer."
        description="No suitability information has been recorded for this layer yet."
        actionLabel="Add suitability"
        onAction={() => navigate('create')}
      />
    )

  const deleteTarget = suitability.find((s) => s.use_case_slug === deleteUseCaseSlug)
  return (
    <Box
      sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      {suitability.map((item) => {
        const isExpanded = currentUseCase === item.use_case_slug
        return (
          <Accordion
            key={item.use_case_slug}
            expanded={isExpanded}
            onChange={(_, open) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev)

                if (open) {
                  next.set('current', item.use_case_slug)
                } else {
                  next.delete('current')
                }

                return next
              })
            }}
            disableGutters
            elevation={0}
            sx={{
              borderBottom: '0.5px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1, minHeight: 48 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%', pr: 1 }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {item.use_case_name}
                </Typography>
                <SuitabilityBadge letterGrade={item.suitability_grade} size="sm" />{' '}
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              {isAdmin && (
                <Box sx={{ display: 'flex', alignItems: 'end' }}>
                  <IconButton
                    size="small"
                    sx={{ paddingX: 1, paddingY: 0, m: 0 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`${item.use_case_slug}/edit`)
                    }}
                    color="default"
                  >
                    <CreateIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ paddingX: 1, paddingY: 0, m: 0 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteUseCaseSlug(item.use_case_slug)
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Box sx={{ fontSize: 14, lineHeight: 1.7, mb: 2 }}>
                <ReactMarkdown>{item.suitability_statement_md ?? ''}</ReactMarkdown>
              </Box>
            </AccordionDetails>
          </Accordion>
        )
      })}

      <Dialog open={!!deleteUseCaseSlug} onClose={() => setDeleteUseCaseSlug(null)}>
        <DialogTitle>Delete suitability</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the suitability for{' '}
          <strong>{deleteTarget?.use_case_name}</strong>? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUseCaseSlug(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isPending}
            onClick={() => {
              if (!deleteUseCaseSlug) return
              deleteSuitability(
                { useCaseSlug: deleteUseCaseSlug },
                {
                  onSuccess: () => {
                    setDeleteUseCaseSlug(null)
                    toast({ title: 'Suitability deleted', type: 'success', position: 'top-center' })
                  },
                  onError: () => setDeleteUseCaseSlug(null),
                },
              )
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
